/* ==========================================================================
   SearcherFli > Client > Passenger > Panel
   ========================================================================== */

/* Modules */
const { html } = require('common-tags');
const { toNode } = require('@bluekiri/alexandria/helpers/domHelpers');

const TooltipTemplate = require('@bluekiri/alexandria-contrib-components/components/tooltip/template');
const Tooltip = require('@bluekiri/alexandria-contrib-components/components/tooltip/client/js');

const ButtonTemplate = require('@bluekiri/alexandria-contrib-components/components/button-rxjs/template');
const Button = require('@bluekiri/alexandria-contrib-components/components/button-rxjs/client/js');

const { fromEvent } = require('rxjs');
const { take, filter } = require('rxjs/operators');

const { addData, addStyle } = require('@bluekiri/alexandria/helpers/dataHelpers');

const { ADULTS, CHILDREN, BABIES } = require('../constants');

const { createAdults, initAdults, destroyAdults } = require('./adults');
const { createBabies, initBabies, destroyBabies } = require('./babies');
const { createChildren, initChildren, destroyChildren } = require('./children');

const configuration = require('../../config');
const actions = require('../../store/actions');

/* Render title */
const renderTitle = title => {
    if (!title) {
        return html('');
    }

    return html(`<p class='searcher__title'>${title}</p>`);
};

/* Render footer */
const renderFooter = literals => {
    const { closePanelLabel = '', ageAtTravelTimeLabel = '' } = literals;

    const button = ButtonTemplate(
        addStyle(
            addData(
                {
                    content: closePanelLabel
                },
                { type: configuration().type.acceptButton }
            ),
            [configuration().css.actionButton]
        )
    );

    return html(`
        <div class='footer'>
            ${ageAtTravelTimeLabel ? `<p>${ageAtTravelTimeLabel}</p>` : ''}
            ${button}
        </div>
    `);
};

/* Create */
function createPanel(instance) {
    /* Data */
    const { passengers } = instance.store.select('data');

    /* Get base config for counter from instance config and set new values */
    const values = Object.keys(instance.config.passengers).reduce((acc, cur) => {
        if (cur === ADULTS || cur === CHILDREN || cur === BABIES) {
            return {
                ...acc,
                [cur]: {
                    ...instance.config.passengers[cur],
                    value: passengers[cur]
                }
            };
        }

        return acc;
    }, instance.config.passengers);

    /* Get values */
    const { title = false, adults = false, children = false, babies = false } = values;

    /* Data */
    const { context, literals } = instance.config;

    /* Template */
    const template = TooltipTemplate(
        addData(
            {
                content: `
                        ${renderTitle(title)}
                        <div class='searcher__wrapper'>
                            ${createAdults(instance, adults)}
                            ${createChildren(instance, children)}
                            ${createBabies(instance, babies)}
                        </div>
                        ${renderFooter(literals)}
                    `,
                negative: false,
                placement: 'bottom-end'
            },
            {
                type: configuration().type.passengers
            }
        ),
        context
    );

    /* Return node */
    return toNode(template)[0];
}

/* Add */
function addPanel(instance, trigger) {
    const panel = instance;

    /* Node */
    const node = createPanel(instance);

    /* Add to DOM */
    instance.element.appendChild(node);

    /* Reference */
    panel.passengerPanel = Tooltip(`[data-component='Tooltip'][data-type=${configuration().type.passengers}]`, {
        trigger
    });

    /* Init */
    fromEvent(node, 'transitionend')
        .pipe(take(1))
        .subscribe(() => {
            initPanel(instance);
        });

    /* Visible */
    setTimeout(() => node.classList.add(configuration().css.visible), 50);
}

/* Init */
function initPanel(instance) {
    const panel = instance;

    /* Data */
    const { adults = false, children = false, babies = false } = instance.config.passengers;

    /* Adults */
    initAdults(instance, adults);

    /* Children */
    initChildren(instance, children);

    /* Babies */
    initBabies(instance, babies);

    /* Button */
    panel.tooltipAcceptPassengersButton = Button(
        `[data-type=${configuration().type.passengers}] 
        [data-component='ButtonRxjs'][data-type='${configuration().type.acceptButton}']`
    );

    /* Subscriptions */
    panel.subscriptions = {
        ...instance.subscriptions,
        closePassengerPanelClick: fromEvent(document, 'click')
            .pipe(
                filter(() => !!instance.passengerPanel),
                filter(event => !event.target.closest('[data-component=Tooltip]')),
                take(1)
            )
            .subscribe(() => {
                /* Remove */
                destroyPanel(instance);
            }),
        closePassengerPanelKey: fromEvent(window, 'keydown')
            .pipe(
                filter(event => !!instance.passengerPanel || event.keyCode === 27),
                take(1)
            )
            .subscribe(() => {
                /* Remove */
                destroyPanel(instance);
            }),
        closePassengerPanelButton: panel.tooltipAcceptPassengersButton.events.click$.subscribe(() => {
            /* Remove */
            destroyPanel(instance);
        })
    };

    /* Reactions */
    panel.reactions = {
        ...panel.reactions,
        changePassengers: instance.store.subscribe((state, action) => {
            if (action.type !== actions.types.CHANGE_PASSENGERS) return;

            if (instance.adults.counter && instance.children.counter && instance.babies.counter) {
                if (state.data.passengersCount === 9) {
                    instance.adults.counter.increment.actions.disable();
                    instance.children.counter.increment.actions.disable();
                    instance.babies.counter.increment.actions.disable();
                    return;
                }
                instance.adults.counter.increment.actions.enable();
                instance.children.counter.increment.actions.enable();
                instance.babies.counter.increment.actions.enable();
            }
        })
    };
}

/* Destroy */
function destroyPanel(instance) {
    const panel = instance;

    /* Element */
    const { element } = instance.passengerPanel;

    if (!instance.passengerPanel) {
        return;
    }

    /* Unsubscribe */
    panel.subscriptions.closePassengerPanelClick.unsubscribe();
    delete panel.subscriptions.closePassengerPanelClick;

    panel.subscriptions.closePassengerPanelButton.unsubscribe();
    delete panel.subscriptions.closePassengerPanelButton;

    panel.subscriptions.closePassengerPanelKey.unsubscribe();
    delete panel.subscriptions.closePassengerPanelKey;

    fromEvent(element, 'transitionend')
        .pipe(take(1))
        .subscribe(() => {
            /* Data */
            const { adults = false, children = false, babies = false } = instance.config.passengers;

            /* Adults */
            destroyAdults(instance, adults);

            /* Children */
            destroyChildren(instance, children);

            /* Babies */
            destroyBabies(instance, babies);

            /* Button */
            panel.tooltipAcceptPassengersButton.actions.destroy();
            delete panel.tooltipAcceptPassengersButton;

            /* Remove from DOM */
            /* Reference */
            instance.passengerPanel.actions.destroy();
            delete panel.passengerPanel;
        });

    /* Visible */
    setTimeout(() => element.classList.remove(configuration().css.visible), 50);
}

/* Exports */
module.exports = {
    addPanel,
    destroyPanel
};
